/* Core */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';
import { getTodayDate } from '~/utils/datetime';
import { downloadInvoicePdf, submitFormData, submitFormDataAndDownloadPdf } from './asyncThunks';

const defaultEntityData: IEntityData = {
  companyName: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  email: '',
  phone: '',
  website: '',
};

const defaultItem: IFormItem = {
  sku: '',
  description: '',
  quantity: '1',
  price: '0',
};

const DEFAULT_FORM: IFormData = {
  logo: null,
  logoUrl: '',
  invoiceNumber: '',
  invoiceDate: getTodayDate(),
  dueDate: getTodayDate(),
  poNumber: '',
  notes: '',
  terms: '',
  currency: 'USD',
  discount: '',
  discountType: 'flat',
  shipping: '',
  shippingType: 'flat',
  tax: '',
  taxType: 'flat',
  sellerDetails: { ...defaultEntityData },
  buyerDetails: { ...defaultEntityData },
  shippingDetails: { ...defaultEntityData },
  isShippingUnique: false,
  items: [{ ...defaultItem }],
  template_id: 'inv0',
  client_uuid: '',
  isShippingEnabled: true,
  saveTermsAsDefault: false,
  saveNotesAsDefault: false,
};

export const formState: FormSliceState = {
  isSubmitLoading: false,
  isLoading: false,
  targetSection: null,
  isDownloadLoading: false,
  formData: { ...DEFAULT_FORM },
  invoiceId: null,

};

export const formSlice = createSlice({
  name: 'form',
  initialState: formState,
  reducers: {
    setValue: (state, action: PayloadAction<Partial<FormSliceState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setTargetSection: (state, action: PayloadAction<string | null>) => {
      state.targetSection = action.payload;
    },
    resetForm: state => {
      state.formData = { ...DEFAULT_FORM };
      state.invoiceId = null;
    },
    setInvoiceId: (state, action: PayloadAction<string | null>) => {
      state.invoiceId = action.payload;
    },

  },

  extraReducers: builder => {
    builder
      .addCase(submitFormData.pending, state => {
        state.isSubmitLoading = true;
      })
      .addCase(submitFormData.fulfilled, state => {
        state.isSubmitLoading = false;
      })
      .addCase(submitFormData.rejected, state => {
        state.isSubmitLoading = false;
      })

      .addCase(submitFormDataAndDownloadPdf.pending, state => {
        state.isSubmitLoading = true;
      })
      .addCase(submitFormDataAndDownloadPdf.fulfilled, state => {
        state.isSubmitLoading = false;
      })
      .addCase(submitFormDataAndDownloadPdf.rejected, state => {
        state.isSubmitLoading = false;
      })

      .addCase(downloadInvoicePdf.pending, state => {
        state.isDownloadLoading = true;
      })
      .addCase(downloadInvoicePdf.fulfilled, state => {
        state.isDownloadLoading = false;
      })
      .addCase(downloadInvoicePdf.rejected, state => {
        state.isDownloadLoading = false;
      });
  },
});

/* Types */

export interface IEntityData {
  companyName: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  email: string;
  phone: string;
  website: string;
}

export interface IFormItem {
  id?: number;
  sku: string;
  description: string;
  quantity: string;
  price: string;
}

export interface IFormData {
  logo: null | File | Blob;
  logoUrl: string;
  invoiceNumber: string;
  invoiceDate: string | Dayjs;
  dueDate: string | Dayjs;
  poNumber: string;
  notes: string;
  terms: string;
  currency: string;
  discount: string;
  discountType: 'flat' | 'percent';
  shipping: string;
  shippingType: 'flat' | 'percent';
  tax: string;
  taxType: 'flat' | 'percent';
  sellerDetails: IEntityData;
  buyerDetails: IEntityData;
  shippingDetails: IEntityData;
  isShippingUnique: boolean;
  items: Array<IFormItem>;
  template_id: string;
  client_uuid: string;
  invoiceUuid?: string;
  isShippingEnabled: boolean;
  saveTermsAsDefault: boolean;
  saveNotesAsDefault: boolean;
}

export interface FormSliceState {
  isLoading: boolean;
  formData: IFormData;
  isSubmitLoading: boolean;
  isDownloadLoading: boolean;
  targetSection: string | null;
  invoiceId: string | null;
}

export default formSlice.reducer;
